import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem,
  Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon, EditOutlined as EditIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import apiService from '../../services/api';
import { DomainDnsRecord } from '../../types/domainRegistry';
import { Confirm, DomainTabProps, ModeNotice, TabBody, dnsConfigOf, errorText } from './shared';

const EDITABLE_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT'];
const TTL_OPTIONS = [300, 900, 1800, 3600, 14400, 43200, 86400];

interface Draft { type: string; name: string; content: string; ttl: number; prio: string }
const EMPTY: Draft = { type: 'A', name: '@', content: '', ttl: 3600, prio: '10' };

const DnsRecordsTab: React.FC<DomainTabProps & { goTo?: (tab: string) => void }> = ({ serviceId, domain, info, goTo }) => {
  const mode = dnsConfigOf(info);
  const [records, setRecords] = useState<DomainDnsRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);
  const [editing, setEditing] = useState<{ record?: DomainDnsRecord } | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<DomainDnsRecord | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.domainRequest<{ records: DomainDnsRecord[] }>('get', serviceId, '/dns-records');
      setRecords(data.records || []);
    } catch (e) {
      setRecords([]);
      setMsg({ severity: 'error', text: errorText(e, 'Could not load the DNS records.') });
    } finally {
      setLoading(false);
    }
  }, [serviceId]);

  useEffect(() => { load(); }, [load]);

  const hostingMode = mode === null || mode === 4;
  const openAdd = () => { setDraft(EMPTY); setDialogError(null); setEditing({}); };
  const openEdit = (r: DomainDnsRecord) => {
    setDraft({ type: r.record_type, name: r.host_name || '@', content: r.content, ttl: r.ttl || 3600, prio: r.prio || '10' });
    setDialogError(null);
    setEditing({ record: r });
  };

  const save = async () => {
    setSaving(true);
    setDialogError(null);
    const body = { type: draft.type, name: draft.name.trim() || '@', content: draft.content.trim(), ttl: Number(draft.ttl), prio: draft.type === 'MX' ? Number(draft.prio) : null };
    try {
      if (editing?.record) await apiService.domainRequest('put', serviceId, `/dns-records/${encodeURIComponent(editing.record.record_id)}`, body);
      else await apiService.domainRequest('post', serviceId, '/dns-records', body);
      setEditing(null);
      setMsg({ severity: 'success', text: editing?.record ? 'Record updated.' : 'Record added.' });
      await load();
    } catch (e) {
      setDialogError(errorText(e, 'Could not save the record.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      await apiService.domainRequest('delete', serviceId, `/dns-records/${encodeURIComponent(deleting.record_id)}`, undefined, {
        label: `${deleting.record_type} ${deleting.host_name} → ${deleting.content}`,
      });
      setDeleting(null);
      setMsg({ severity: 'success', text: 'Record deleted.' });
      await load();
    } catch (e) {
      setDeleting(null);
      setMsg({ severity: 'error', text: errorText(e, 'Could not delete the record.') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <TabBody>
      <ModeNotice current={mode} needs={4} what="DNS records" onGoToNameservers={goTo ? () => goTo('nameservers') : undefined} />
      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          DNS records for <strong>{domain}</strong> in Synergy's zone. A, AAAA, CNAME, MX and TXT records can be edited here.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />} onClick={load} disabled={loading}>Reload</Button>
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openAdd} disabled={!hostingMode}>Add record</Button>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ overflow: 'auto', borderRadius: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell><TableCell>Type</TableCell><TableCell>Content</TableCell>
              <TableCell>TTL</TableCell><TableCell>Priority</TableCell><TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {records.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>{loading ? 'Loading…' : 'No DNS records.'}</TableCell></TableRow>
            )}
            {records.map((r) => {
              const editable = hostingMode && EDITABLE_TYPES.includes(r.record_type);
              return (
                <TableRow key={r.record_id || `${r.host_name}-${r.record_type}-${r.content}`} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{r.host_name}</TableCell>
                  <TableCell><Chip size="small" label={r.record_type} variant="outlined" /></TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all', maxWidth: 420 }}>{r.content}</TableCell>
                  <TableCell>{r.ttl ?? '—'}</TableCell>
                  <TableCell>{r.prio || '—'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {editable ? (
                      <>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">{hostingMode ? 'Read only' : ''}</Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={!!editing} onClose={saving ? undefined : () => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.record ? 'Edit DNS record' : 'Add DNS record'}</DialogTitle>
        <DialogContent dividers>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField select label="Type" value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })} sx={{ width: 140 }}>
                {EDITABLE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField label="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} fullWidth helperText="@ for the domain itself, or e.g. www" />
            </Box>
            <TextField
              label="Content"
              value={draft.content}
              onChange={(e) => setDraft({ ...draft, content: e.target.value })}
              fullWidth
              multiline={draft.type === 'TXT'}
              minRows={draft.type === 'TXT' ? 2 : 1}
              helperText={{ A: 'IPv4 address', AAAA: 'IPv6 address', CNAME: 'Target host name', MX: 'Mail server host name', TXT: 'Text value' }[draft.type]}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField select label="TTL" value={draft.ttl} onChange={(e) => setDraft({ ...draft, ttl: Number(e.target.value) })} sx={{ width: 200 }}>
                {Array.from(new Set([...TTL_OPTIONS, draft.ttl])).sort((a, b) => a - b).map((t) => <MenuItem key={t} value={t}>{t} seconds</MenuItem>)}
              </TextField>
              {draft.type === 'MX' && (
                <TextField label="Priority" type="number" value={draft.prio} onChange={(e) => setDraft({ ...draft, prio: e.target.value })} sx={{ width: 140 }} />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !draft.content.trim()}>{saving ? <CircularProgress size={20} /> : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Confirm
        open={!!deleting}
        busy={saving}
        danger
        title="Delete this DNS record?"
        text={deleting ? `${deleting.record_type} ${deleting.host_name} → ${deleting.content}` : ''}
        confirmLabel="Delete"
        onCancel={() => setDeleting(null)}
        onConfirm={remove}
      />
    </TabBody>
  );
};

export default DnsRecordsTab;
